import React, { useEffect, useState } from 'react';
import {
    Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, TextField, Typography,
} from '@mui/material';
import { formatPhone } from '../../voice/callFormat';
import { clockValue, formatListDate, missedLine, uploadedDay, whoLines } from './missedCallFormat';

/**
 * 부재 시간 고치기 — the time a screenshot was read as, corrected by hand (plan §6.2).
 *
 * The reading is usually right but not always, and a past day's upload has no time at all (시간 모름), so this goes
 * both ways: type a time, or tick 시간 모름 to take one away.
 *
 * Only a wall clock is edited. The server pins it to the row's own `list_date` in the office's zone, so the day can
 * never drift and this dialog never deals in dates. Correcting the time does NOT reopen a 완료 row — a correction is
 * not a new call — which is worth saying on screen, because an import seeing a newer time does exactly that.
 */
const MissedTimeDialog = ({ row, open, onClose, onSave }) => {
    const [time, setTime] = useState('');
    const [unknown, setUnknown] = useState(false);
    const [busy, setBusy] = useState(false);

    // Re-read whenever the dialog opens, or the row changes underneath it (another admin's edit arrives live).
    useEffect(() => {
        if (!open || !row) return;
        setUnknown(Boolean(row.missed_time_unknown));
        setTime(row.missed_time_unknown ? '' : clockValue(row.last_missed_at));
        setBusy(false);
    }, [open, row?.id, row?.last_missed_at, row?.missed_time_unknown]);   // eslint-disable-line react-hooks/exhaustive-deps

    if (!row) return null;

    const was = row.missed_time_unknown ? '' : clockValue(row.last_missed_at);
    const changed = unknown ? !row.missed_time_unknown : Boolean(time) && time !== was;

    const save = async () => {
        if (!changed || busy) return;
        setBusy(true);
        await onSave(row, unknown ? null : time);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" disableEnforceFocus>
            <DialogTitle>부재 시간 고치기</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {uploadedDay(row)} #{row.daily_seq} · {formatPhone(row.phone_number)}
                    {whoLines(row).length ? ` · ${whoLines(row).map((l) => l.text).join(', ')}` : ''}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>현재: {missedLine(row) || '부재 시간 없음'}</Typography>

                <TextField
                    label="부재 시간"
                    type="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); save(); } }}
                    disabled={unknown || busy}
                    fullWidth
                    autoFocus
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ 'aria-label': '부재 시간' }}
                />
                <FormControlLabel
                    sx={{ mt: 1 }}
                    control={<Checkbox checked={unknown} disabled={busy} onChange={(e) => setUnknown(e.target.checked)} />}
                    label="시간 모름 (날짜만 알고 있음)"
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    {formatListDate(row.list_date)} 안의 시간으로 저장되며, 완료된 행이 다시 열리지는 않습니다. 변경 내역은 기록에 남습니다.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>취소</Button>
                <Button variant="contained" onClick={save} disabled={!changed || busy}>저장</Button>
            </DialogActions>
        </Dialog>
    );
};

export default MissedTimeDialog;
